import { BaseSocket } from '@/sockets/base.socket';
import { useProjectStore } from '../stores/project.store';
import {
  ProjectSocketEvents,
  type ProjectSocketConfigurationTaskDTO,
  type ProjectSocketProjectDTO,
} from './project.socket.interface';
import { event } from 'vue-gtag';

/**
 * Handles WebSocket connections for ecommerce-related real-time communications.
 */
export class ProjectSocket extends BaseSocket {
  private static readonly CHANNEL_PREFIX = 'private-clee.projects';

  /**
   * Creates an instance of ProjectSocket.
   * @param {number} project_id - The ID of the project to subscribe to.
   */
  constructor(project_id: number) {
    const channelName = `${ProjectSocket.CHANNEL_PREFIX}.${project_id}`;
    super(channelName);
  }

  protected setupEventListeners(): void {
    const onProjectStateChanged = () => {
      const projectStore = useProjectStore();
      projectStore.refetch();
    };

    this.onProjectUpdated(onProjectStateChanged);
    this.onProjectCreated(onProjectStateChanged);
    this.onProjectSettingUpdated(onProjectStateChanged);
    this.onOnboardingTranscriptUpdated(onProjectStateChanged);
    this.onProjectBriefUpdated(onProjectStateChanged);
    this.onBriefStatusChanged(onProjectStateChanged);

    this.onConfigurationTaskUpdated((data) => {
      const projectStore = useProjectStore();
      projectStore.setLastConfigurationTaskCompletedId(data.task_summary_id);
      projectStore.refetch();
      event(`configuration_task_completed_${data.order}`, {
        configuration_task_id: data.task_id,
        configuration_task: data.task_task,
        project_id: projectStore.current_project_id,
      });
    });
  }

  /**
   * Binds a callback to handle all events.
   * @param {Function} callback - The callback to execute when the  is received.
   */
  onEvent(callback: () => void): void {
    this.bindAll(callback);
  }

  onProjectUpdated(callback: (data: ProjectSocketProjectDTO) => void): void {
    this.bind(ProjectSocketEvents.ProjectUpdated, callback);
  }

  onProjectCreated(callback: (data: ProjectSocketProjectDTO) => void): void {
    this.bind(ProjectSocketEvents.ProjectCreated, callback);
  }

  onProjectSettingUpdated(callback: (data: ProjectSocketProjectDTO) => void): void {
    this.bind(ProjectSocketEvents.ProjectSettingUpdated, callback);
  }

  onOnboardingTranscriptUpdated(callback: (data: ProjectSocketProjectDTO) => void): void {
    this.bind(ProjectSocketEvents.OnboardingTranscriptUpdated, callback);
  }

  /**
   * Binds a callback to handle project updated events.
   * @param callback - The callback to execute when the project is updated.
   */
  onProjectBriefUpdated(callback: (data: ProjectSocketProjectDTO) => void): void {
    this.bind(ProjectSocketEvents.ProjectBriefUpdated, callback);
  }

  /**
   * Binds a callback to handle brief status changed events.
   * @param callback - The callback to execute when the brief status is changed.
   */
  onBriefStatusChanged(callback: (data: ProjectSocketProjectDTO) => void): void {
    this.bind(ProjectSocketEvents.BriefStatusChanged, callback);
  }

  /**
   * Binds a callback to handle configuration task updated events.
   * @param callback - The callback to execute when the configuration task is updated.
   */
  onConfigurationTaskUpdated(callback: (data: ProjectSocketConfigurationTaskDTO) => void): void {
    this.bind(ProjectSocketEvents.ConfigurationTaskUpdated, callback);
  }
}
